import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, XCircleIcon, DownloadIcon, SearchIcon, FilterIcon, MoreHorizontalIcon, SendIcon, RefreshCwIcon, FileTextIcon, InfoIcon } from 'lucide-react';
import { PaymentItem, BulkPaymentBatch } from './BulkPayments';
export function BulkPaymentDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  // Mock batch data
  const batch: BulkPaymentBatch = {
    id: id || '1',
    name: 'December Invoices',
    createdAt: '2023-12-15T09:30:00',
    totalAmount: 12450.75,
    currency: 'USD',
    totalItems: 45,
    completedItems: 38,
    status: 'completed',
    collectionRate: 84.4
  };
  // Mock payment items
  const paymentItems: PaymentItem[] = [{
    id: '1',
    customerName: 'John Smith',
    email: 'john.smith@example.com',
    phone: '+1 (555) 123-4567',
    amount: '349.99',
    currency: 'USD',
    description: 'Monthly subscription - Premium Plan',
    reference: 'INV-2023-12-001',
    status: 'paid'
  }, {
    id: '2',
    customerName: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    phone: '+1 (555) 987-6543',
    amount: '199.50',
    currency: 'USD',
    description: 'Consulting services - December',
    reference: 'INV-2023-12-002',
    status: 'paid'
  }, {
    id: '3',
    customerName: 'Michael Brown',
    email: 'mbrown@example.com',
    phone: '+1 (555) 456-7890',
    amount: '599.00',
    currency: 'USD',
    description: 'Website development - Phase 1',
    reference: 'INV-2023-12-003',
    status: 'pending'
  }, {
    id: '4',
    customerName: 'Emily Wilson',
    email: 'e.wilson@example.com',
    phone: '+1 (555) 234-5678',
    amount: '149.99',
    currency: 'USD',
    description: 'Monthly subscription - Basic Plan',
    reference: 'INV-2023-12-004',
    status: 'paid'
  }, {
    id: '5',
    customerName: 'David Lee',
    email: 'david.lee@example.com',
    phone: '+1 (555) 876-5432',
    amount: '299.99',
    currency: 'USD',
    description: 'Marketing services - December',
    reference: 'INV-2023-12-005',
    status: 'expired'
  }, {
    id: '6',
    customerName: 'Jennifer Garcia',
    email: 'j.garcia@example.com',
    phone: '+1 (555) 345-6789',
    amount: '499.00',
    currency: 'USD',
    description: 'Product photography - Package B',
    reference: 'INV-2023-12-006',
    status: 'paid'
  }, {
    id: '7',
    customerName: 'Robert Martinez',
    email: 'r.martinez@example.com',
    phone: '+1 (555) 765-4321',
    amount: '799.50',
    currency: 'USD',
    description: 'Equipment rental - December',
    reference: 'INV-2023-12-007',
    status: 'paid'
  }];
  // Filter payments based on search and status filter
  const filteredPayments = paymentItems.filter(item => {
    const matchesSearch = item.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || item.email.toLowerCase().includes(searchTerm.toLowerCase()) || item.description.toLowerCase().includes(searchTerm.toLowerCase()) || item.reference.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter ? item.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });
  // Count payments by status
  const statusCounts = paymentItems.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2411">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-2412" />
            Paid
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-2413">
            <ClockIcon size={12} className="mr-1" data-id="element-2414" />
            Pending
          </span>;
      case 'expired':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2415">
            <XCircleIcon size={12} className="mr-1" data-id="element-2416" />
            Expired
          </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-2417">
            <AlertCircleIcon size={12} className="mr-1" data-id="element-2418" />
            Failed
          </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2419">
            <XCircleIcon size={12} className="mr-1" data-id="element-2420" />
            Cancelled
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500" data-id="element-2421">
            {status}
          </span>;
    }
  };
  // Navigate back to the previous page
  const handleNavigateBack = () => {
    navigate(-1); // This uses the browser's history to go back to the previous page
  };
  return <div className="space-y-6" data-id="element-2422">
      {/* Back button */}
      <button onClick={handleNavigateBack} className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-2423">
        <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-2424" />
        Back to Payment Form
      </button>
      {/* Batch summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2425">
        <div className="flex justify-between items-start" data-id="element-2426">
          <div data-id="element-2427">
            <h2 className="text-xl font-semibold text-gray-800" data-id="element-2428">
              {batch.name}
            </h2>
            <p className="text-gray-500 mt-1" data-id="element-2429">
              Created on {formatDate(batch.createdAt)}
            </p>
          </div>
          <div className="flex space-x-3" data-id="element-2430">
            <button className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors" data-id="element-2431">
              <DownloadIcon size={16} className="mr-1.5" data-id="element-2432" />
              Export
            </button>
            <button className="flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors" data-id="element-2433">
              <SendIcon size={16} className="mr-1.5" data-id="element-2434" />
              Send Reminders
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6" data-id="element-2435">
          <div className="bg-gray-50 p-4 rounded-lg" data-id="element-2436">
            <div className="text-sm text-gray-500 mb-1" data-id="element-2437">Total Amount</div>
            <div className="text-xl font-semibold text-gray-900" data-id="element-2438">
              {batch.currency} {batch.totalAmount.toFixed(2)}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg" data-id="element-2439">
            <div className="text-sm text-gray-500 mb-1" data-id="element-2440">Collection Rate</div>
            <div className="flex items-center" data-id="element-2441">
              <div className="text-xl font-semibold text-gray-900 mr-2" data-id="element-2442">
                {batch.collectionRate}%
              </div>
              <div className="w-full max-w-[100px] bg-gray-200 rounded-full h-2" data-id="element-2443">
                <div className={`h-2 rounded-full ${batch.collectionRate >= 80 ? 'bg-green-500' : batch.collectionRate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                width: `${batch.collectionRate}%`
              }} data-id="element-2444"></div>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-1" data-id="element-2445">
              {batch.completedItems} of {batch.totalItems} paid
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg" data-id="element-2446">
            <div className="text-sm text-gray-500 mb-1" data-id="element-2447">Status</div>
            <div className="flex items-center" data-id="element-2448">
              {batch.status === 'completed' ? <CheckCircleIcon size={18} className="text-green-500 mr-1.5" data-id="element-2449" /> : batch.status === 'processing' ? <ClockIcon size={18} className="text-blue-500 mr-1.5" data-id="element-2450" /> : <AlertCircleIcon size={18} className="text-red-500 mr-1.5" data-id="element-2451" />}
              <span className="text-lg font-medium text-gray-900 capitalize" data-id="element-2452">
                {batch.status}
              </span>
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg" data-id="element-2453">
            <div className="text-sm text-gray-500 mb-1" data-id="element-2454">Payment Requests</div>
            <div className="text-xl font-semibold text-gray-900" data-id="element-2455">
              {batch.totalItems}
            </div>
            <div className="flex items-center text-xs text-gray-500 mt-1" data-id="element-2456">
              <RefreshCwIcon size={12} className="mr-1" data-id="element-2457" />
              Last updated 5 minutes ago
            </div>
          </div>
        </div>
      </div>
      {/* Payment requests list */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-2458">
        <h3 className="text-lg font-medium text-gray-800 mb-6" data-id="element-2459">
          Payment Requests
        </h3>
        {/* Search and filter */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-3 sm:space-y-0" data-id="element-2460">
          <div className="flex flex-wrap gap-2" data-id="element-2461">
            <button className={`px-3 py-1 text-sm rounded-md ${statusFilter === null ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setStatusFilter(null)} data-id="element-2462">
              All ({paymentItems.length})
            </button>
            {Object.entries(statusCounts).map(([status, count]) => <button key={status} className={`px-3 py-1 text-sm rounded-md ${statusFilter === status ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setStatusFilter(status === statusFilter ? null : status)} data-id="element-2463">
                {status.charAt(0).toUpperCase() + status.slice(1)} ({count})
              </button>)}
          </div>
          <div className="relative w-full sm:w-64" data-id="element-2464">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2465">
              <SearchIcon size={16} className="text-gray-400" data-id="element-2466" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search payments..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2467" />
          </div>
        </div>
        {/* Payments table */}
        <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-2468">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2469">
            <thead className="bg-gray-50" data-id="element-2470">
              <tr data-id="element-2471">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2472">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2473">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2474">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2475">
                  Reference
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2476">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2477">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2478">
              {filteredPayments.map(payment => <tr key={payment.id} className="hover:bg-gray-50" data-id="element-2479">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2480">
                    <div className="text-sm font-medium text-gray-900" data-id="element-2481">
                      {payment.customerName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-2482">{payment.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2483">
                    <div className="text-sm font-medium text-gray-900" data-id="element-2484">
                      {payment.currency} {parseFloat(payment.amount).toFixed(2)}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-2485">
                    <div className="text-sm text-gray-900 max-w-xs truncate" data-id="element-2486">
                      {payment.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2487">
                    {payment.reference}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2488">
                    {getStatusBadge(payment.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2489">
                    <div className="relative group" data-id="element-2490">
                      <button className="text-gray-400 hover:text-gray-500" data-id="element-2491">
                        <MoreHorizontalIcon size={18} data-id="element-2492" />
                      </button>
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 hidden group-hover:block" data-id="element-2493">
                        <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-2494">
                          <SendIcon size={16} className="mr-3 text-gray-400" data-id="element-2495" />
                          Resend
                        </button>
                        <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-2496">
                          <FileTextIcon size={16} className="mr-3 text-gray-400" data-id="element-2497" />
                          View Details
                        </button>
                        {payment.status === 'pending' && <button className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100" data-id="element-2498">
                            <XCircleIcon size={16} className="mr-3 text-red-500" data-id="element-2499" />
                            Cancel Request
                          </button>}
                      </div>
                    </div>
                  </td>
                </tr>)}
              {filteredPayments.length === 0 && <tr data-id="element-2500">
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-2501">
                    No payment requests found
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        {/* Help text */}
        <div className="mt-6 p-4 bg-blue-50 text-blue-700 rounded-md flex items-start" data-id="element-2502">
          <InfoIcon size={18} className="mt-0.5 mr-3 flex-shrink-0" data-id="element-2503" />
          <div className="text-sm" data-id="element-2504">
            <p data-id="element-2505">
              Payment statuses update automatically as customers complete
              payments. You can manually send reminders for pending payments or
              cancel requests that are no longer needed.
            </p>
          </div>
        </div>
      </div>
    </div>;
}